import { getUid } from "ol";
import { GeoJSON } from "ol/format";
import VectorLayer from "ol/layer/Vector";
import { bbox } from "ol/loadingstrategy";
import "ol/ol.css";
import { add } from "date-fns";
import VectorSource from "ol/source/Vector";
import { Fill, Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import url, { getFetchOptions } from "../../../module/fetch.tsx";
import { useGet } from "../../Fetch/useFetch.tsx";
import { TypeModuleRemocra } from "../../ModuleRemocra/ModuleRemocra.tsx";
import MapComponent, { useMapComponent } from "../Map.tsx";
import { useToolbarContext } from "../MapToolbar.tsx";
import { refreshLayerGeoserver } from "../MapUtils.tsx";
import MapToolbarCrise, { useToolbarCriseContext } from "./MapToolbarCrise.tsx";

const MapCrise = ({
  criseId,
  state,
  variant,
}: {
  criseId: string;
  state: string;
  variant: string;
}) => {
  const mapElement = useRef<HTMLDivElement>();

  /** Permet d'afficher les géometries évènements */
  const getLayerUrl = useCallback(
    (extent: number[], projection: { getCode: () => string }) =>
      `/api/crise/evenement/layer?bbox=${extent.join(",")}&srid=${projection.getCode()}&criseId=${criseId}&state=${state}`,
    [criseId, state],
  );

  const listeCouches = useGet(url`/api/crise/${criseId}/get-couches`)?.data;

  const {
    map,
    workingLayer,
    availableLayers,
    addOrRemoveLayer,
    layerListRef,
    projection,
    showOutilI,
    mapToolbarRef,
  } = useMapComponent({
    mapElement: mapElement,
    typeModule: TypeModuleRemocra.CRISE,
    criseId: criseId,
    criseStatutMode: state,
  });

  const dataEvenementLayer = useMemo(() => {
    if (!map) {
      return;
    }
    // on met en place un style transparent pour que les SDIS le définissent directement dans geoserver (invisible à l'oeil nu mais pas totalement transparent pour que les interactions fonctionnent)
    const style = new Style({
      fill: new Fill({ color: "rgba(0, 0, 0, 0.01)" }),
      stroke: new Stroke({ color: "rgba(0, 0, 0, 0.01)", width: 1 }),
      image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({ color: "rgba(0, 0, 0, 0.01)" }),
        fill: new Fill({ color: "rgba(0, 0, 0, 0.01)" }),
      }),
    });

    const vectorSource = new VectorSource({
      strategy: bbox,
      format: new GeoJSON({
        dataProjection: projection,
        featureProjection: projection,
      }),
    });

    const layer = new VectorLayer({
      source: vectorSource,
      style,
      opacity: 1,
      visible: true,
      minZoom: 8,
      maxResolution: 99999,
      zIndex: 9999,
    });

    map.addLayer(layer);
    return layer;
  }, [map, projection]);

  useEffect(() => {
    if (!map || !dataEvenementLayer) {
      return;
    }
    const source = dataEvenementLayer.getSource()!;
    source?.clear();

    source!.setLoader(
      async (
        extent: number[],
        _: any,
        projection: { getCode: () => string },
        success: (arg0: any) => void,
        failure: () => void,
      ) => {
        try {
          const res = await fetch(
            url`${getLayerUrl(extent, projection)}`,
            getFetchOptions({ method: "GET" }),
          );
          const features = source.getFormat().readFeatures(await res.json());
          source.addFeatures(features);
          success(features);
        } catch {
          source.removeLoadedExtent(extent);
          failure();
        }
      },
    );

    source.refresh();
  }, [dataEvenementLayer, getLayerUrl, map]);

  const {
    tools: extraTools,
    handleCloseEvent,
    showCreateEvent,
    showListEvent,
    showListDocument,
    showPersonalReports,
    setShowPersonalReports,
    setShowListEvent,
    setShowCreateEvent,
    setShowListDocument,
    listeEventId,
    setSousTypeElement,
    geometryElement,
    setGeometryReportCode,
    reportGeometryElement,
    sousTypeElement,
  } = useToolbarCriseContext({
    map,
    workingLayer,
    dataEvenementLayer,
  });

  const { toggleTool, activeTool, infoOutilI, handleCloseInfoI } =
    useToolbarContext({
      availableLayers: availableLayers,
      map: map,
      workingLayer: workingLayer,
      extraTools: extraTools,
    });

  /**
   * Met à jour la liste des couches actives sur la carte en fonction des couches WMS sélectionnées.
   */
  const listeDesCouches = useMemo(() => {
    if (!listeCouches) {
      return [];
    }
    return availableLayers.map((group: any) => ({
      ...group,
      layers: group.layers.filter((layer: any) => {
        const isActive = listeCouches.some(
          (c: any) => c.code === layer.code && c[state.toLowerCase()],
        );
        return isActive;
      }),
    }));
  }, [availableLayers, listeCouches, state]);

  useEffect(() => {
    if (!map || !listeCouches) {
      return;
    }

    const activeCoucheCodes = new Set(
      listeCouches
        .filter((c: any) => c[state.toLowerCase()])
        .map((c: any) => c.code),
    );

    availableLayers
      .flatMap((group: any) => group.layers)
      .forEach((layer: any) => {
        const currentLayer = map
          .getLayers()
          .getArray()
          .find((l: any) => l === layer.openlayer);

        if (currentLayer) {
          currentLayer.setVisible(activeCoucheCodes.has(layer.code));
        }
      });
  }, [availableLayers, listeCouches, map, state]);

  return (
    <MapComponent
      key={state}
      map={map}
      showZoomPlace={false}
      mapElement={mapElement}
      availableLayers={listeDesCouches}
      addOrRemoveLayer={addOrRemoveLayer} // les éléments de fonds IGN
      layerListRef={layerListRef} // les éléments de fonds IGN
      mapToolbarRef={mapToolbarRef} // les boutons à modifier / rajouter
      toggleTool={toggleTool}
      outilI={infoOutilI}
      showOutilI={showOutilI}
      handleCloseInfoI={handleCloseInfoI}
      activeTool={activeTool}
      toolbarElement={
        mapToolbarRef.current &&
        dataEvenementLayer && (
          <MapToolbarCrise
            setGeometryReportCode={setGeometryReportCode}
            state={state}
            map={map}
            criseId={criseId}
            handleCloseEvent={handleCloseEvent}
            listeEventId={listeEventId}
            toggleTool={toggleTool}
            activeTool={activeTool}
            reportGeometryElement={reportGeometryElement}
            geometryElement={geometryElement}
            workingLayer={workingLayer}
            setSousTypeElement={setSousTypeElement}
            sousTypeElement={sousTypeElement}
            dataCriseLayer={dataEvenementLayer}
            showListEvent={showListEvent}
            showListDocument={showListDocument}
            showCreateEvent={showCreateEvent}
            setShowListEvent={setShowListEvent}
            setShowCreateEvent={setShowCreateEvent}
            setShowListDocument={setShowListDocument}
            setShowPersonalReports={setShowPersonalReports}
            showPersonalReports={showPersonalReports}
            variant={variant}
          />
        )
      }
      variant={variant}
    />
  );
};

export default MapCrise;
